import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Close, Download, Info, Save } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { TextButton } from "../../../components/commonComponents/Buttons/ActionButton";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import {
  ListingCellCenter,
  ListingCellLeft,
  ListingHeaderContent,
} from "../../../components/commonComponents/TabularLists/styles";
import ListingDataTableForModal from "../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import {
  HtmlTooltip,
  IconContainer,
  StyledCheckbox,
  StyledDarkRedInformationTypography,
  StyledWhiteCheckbox,
} from "../../../components/commonComponents/StyledComponents/styled";
import { checkTodayDateWithFormat } from "../../../api_calls/utils";
import { getStoresDetailedAvailableHours } from "../../../api_calls/campaignRequests";
import * as xlsx from "xlsx";

// Store Availability filter options for the table
const ALL_AVAILABILITY_OPTION = { label: "All", value: "all" };
const STORE_AVAILABILITY_OPTIONS = [
  ALL_AVAILABILITY_OPTION,
  { label: "Only All Available", value: "all_available" },
  { label: "Only Partially Available", value: "partially_available" },
];

const AVAILABILITY_TONES = {
  all: "#AF3241",
  some: "#F04B55",
  none: "#62626E",
};

const WEEK_COUNT_COL_SX = {
  whiteSpace: "nowrap",
};

const resolveCell = (rowCells, key, storeTotal) => {
  const cell = rowCells?.[key];
  if (cell && typeof cell.count === "number") return cell;
  return { count: 0, total: storeTotal };
};

const availabilityTone = (count, total) => {
  if (!total || count <= 0) return "none";
  if (count >= total) return "all";
  return "some";
};

const StoreAvailabilityLegend = () => (
  <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
    {[
      { tone: "all", label: "All selected stores available" },
      { tone: "some", label: "Some selected stores available" },
      { tone: "none", label: "None of selected stores available" },
    ].map((item) => (
      <Stack key={item.tone} direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: AVAILABILITY_TONES[item.tone],
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: "#62626E" }}>
          {item.label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

// Parses detailedWeekdays — UPPERCASE day keys → TitleCase, total = storeIds.length
const toTitleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const parseWeekdayMatrix = (responseBody, storeTotal) => {
  const matrix = {};
  if (!responseBody || typeof responseBody !== "object") return matrix;

  const dwMap = responseBody.detailedWeekdays;
  if (!dwMap || typeof dwMap !== "object") return matrix;

  for (const dayKey of Object.keys(dwMap)) {
    const dayValue = toTitleCase(dayKey);
    const weeksObj = dwMap[dayKey];
    if (!weeksObj || typeof weeksObj !== "object") continue;
    for (const weekKey of Object.keys(weeksObj)) {
      const weekNum = Number(weekKey);
      const count = weeksObj[weekKey];
      if (typeof count !== "number" || isNaN(weekNum)) continue;
      if (!matrix[dayValue]) matrix[dayValue] = {};
      matrix[dayValue][weekNum] = { count, total: storeTotal };
    }
  }

  return matrix;
};

const DetailedWeekdaySelection = ({ open, onClose, usedFor, availableWeekdays = [] }) => {
  const dispatch = useDispatch();
  const currentStateData = useSelector((state) => state.userSelections);

  // Local selection state — only written to Redux on Apply
  const [localSelectedDays, setLocalSelectedDays] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState(ALL_AVAILABILITY_OPTION);

  const [weekdayMatrix, setWeekdayMatrix] = useState({});

  // -------- Sync: initialize local selection from Redux on popup open --------
  useEffect(() => {
    if (!open) return;

    const currentOptions = currentStateData.new_clip_data?.clip_week_day_options || [];
    setLocalSelectedDays(currentOptions);
    setAvailabilityFilter(ALL_AVAILABILITY_OPTION);
    setWeekdayMatrix({});
  }, [open]);

  // -------- Fetch: detailed weekday availability --------
  useEffect(() => {
    if (!open) return;

    const storeIds = (currentStateData.selected_store_options || [])
      .map((s) => s.id)
      .filter((id) => id !== "all" && typeof id !== "undefined");
    const startDate = currentStateData.new_clip_data?.clip_start_date;
    const endDate = currentStateData.new_clip_data?.clip_end_date;
    const calenderWeeks = currentStateData.new_clip_data?.clip_weeks || [];

    if (storeIds.length === 0 || !startDate || !endDate) {
      setWeekdayMatrix({});
      return;
    }

    let cancelled = false;
    getStoresDetailedAvailableHours({ storeIds, startDate, endDate, calenderWeeks })
      .then((result) => {
        if (cancelled) return;
        if (result.success === true) {
          setWeekdayMatrix(parseWeekdayMatrix(result.data, storeIds.length));
        } else {
          setWeekdayMatrix({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    currentStateData.selected_store_options,
    currentStateData.new_clip_data?.clip_start_date,
    currentStateData.new_clip_data?.clip_end_date,
    currentStateData.new_clip_data?.clip_weeks,
  ]);

  // -------- Sorted calendar week columns (chronological, cross-year safe) --------
  const sortedWeekColumns = useMemo(() => {
    const selectedWeeks = currentStateData.new_clip_data?.clip_weeks || [];
    const allWeeks = currentStateData.campaign_weeks || [];

    // campaign_weeks is already ordered chronologically (built by iterating day by day)
    return allWeeks.filter((w) => selectedWeeks.map(Number).includes(Number(w.value)));
  }, [currentStateData.new_clip_data?.clip_weeks, currentStateData.campaign_weeks]);

  const storeTotal = (currentStateData.selected_store_options || []).filter(
    (s) => s.id !== "all"
  ).length;

  const isRowUnavailable = (row) => {
    if (Object.keys(weekdayMatrix).length === 0 || sortedWeekColumns.length === 0) return false;
    return sortedWeekColumns.every(
      (w) => resolveCell(weekdayMatrix[row.value], w.value, storeTotal).count === 0
    );
  };

  // -------- Filter rows by Store Availability --------
  const filteredRows = useMemo(() => {
    if (!availabilityFilter || availabilityFilter.value === "all") return availableWeekdays;

    return availableWeekdays.filter((row) => {
      const allFull =
        sortedWeekColumns.length > 0 &&
        sortedWeekColumns.every((w) => {
          const cell = resolveCell(weekdayMatrix[row.value], w.value, storeTotal);
          return cell.total > 0 && cell.count === cell.total;
        });
      return availabilityFilter.value === "all_available" ? allFull : !allFull;
    });
  }, [availableWeekdays, availabilityFilter, weekdayMatrix, sortedWeekColumns, storeTotal]);

  // -------- Selection helpers --------
  const isDaySelected = (dayValue) =>
    localSelectedDays.some((d) => d.value === dayValue);

  const toggleDay = (dayOption) => {
    if (isRowUnavailable(dayOption)) return;
    setLocalSelectedDays((prev) =>
      isDaySelected(dayOption.value)
        ? prev.filter((d) => d.value !== dayOption.value)
        : [...prev, dayOption]
    );
  };

  const selectableRows = filteredRows.filter((row) => !isRowUnavailable(row));

  const areAllVisible = () =>
    selectableRows.length > 0 &&
    selectableRows.every((row) => isDaySelected(row.value));

  const toggleSelectAll = (checked) => {
    if (checked) {
      setLocalSelectedDays((prev) => {
        const existing = new Set(prev.map((d) => d.value));
        const toAdd = selectableRows.filter((row) => !existing.has(row.value));
        return [...prev, ...toAdd];
      });
    } else {
      const visibleValues = new Set(selectableRows.map((row) => row.value));
      setLocalSelectedDays((prev) => prev.filter((d) => !visibleValues.has(d.value)));
    }
  };

  // -------- Apply: write local selection to Redux --------
  const handleApply = () => {
    dispatch(
      updateStateData({
        new_clip_data: {
          ...currentStateData.new_clip_data,
          clip_week_days: localSelectedDays.filter((d) => !isRowUnavailable(d)).map((d) => d.value),
          clip_week_day_options: localSelectedDays.filter((d) => !isRowUnavailable(d)),
        },
      })
    );
    onClose();
  };

  // -------- Excel export (selected rows only) --------
  const downloadExcelFile = () => {
    const headers = [
      "Weekday",
      ...sortedWeekColumns.map((w) => `Week ${w.value}`),
    ];

    const rows = localSelectedDays.map((day) => {
      const cellValues = sortedWeekColumns.map((w) => {
        const cell = resolveCell(weekdayMatrix[day.value], w.value, storeTotal);
        return `${cell.count}/${cell.total}`;
      });
      return [day.label, ...cellValues];
    });

    const data = [headers, ...rows];
    const workbook = xlsx.utils.book_new();
    const worksheet = xlsx.utils.aoa_to_sheet(data);
    xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    xlsx.writeFile(
      workbook,
      `${checkTodayDateWithFormat("YYYYMMDD")}_Weekday_Optimization.xlsx`
    );
  };

  // -------- Render --------
  const selectedInView = filteredRows.filter((row) => isDaySelected(row.value)).length;

  // -------- Table columns (shared listing table config) --------
  const columns = [
    // Select checkbox column
    {
      id: "select",
      headerAlign: "center",
      align: "center",
      header: (
        <ListingCellCenter>
          <StyledWhiteCheckbox
            size="small"
            checked={areAllVisible()}
            onChange={(e) => toggleSelectAll(e.target.checked)}
          />
        </ListingCellCenter>
      ),
      render: (row) => {
        const unavailable = isRowUnavailable(row);
        const checkbox = (
          <StyledCheckbox
            size="small"
            checked={isDaySelected(row.value) && !unavailable}
            disabled={unavailable}
            onChange={() => toggleDay(row)}
          />
        );
        return (
          <ListingCellCenter>
            {unavailable ? (
              <HtmlTooltip title="No Store Open" arrow>
                <span>{checkbox}</span>
              </HtmlTooltip>
            ) : (
              checkbox
            )}
          </ListingCellCenter>
        );
      },
    },

    // Weekday label
    {
      id: "weekday",
      header: <ListingHeaderContent>Weekday</ListingHeaderContent>,
      render: (row) => <ListingCellLeft>{row.label}</ListingCellLeft>,
    },

    // One column per selected calendar week
    ...sortedWeekColumns.map((w) => ({
      id: `week_${w.value}`,
      header: <ListingHeaderContent>Week {w.value}</ListingHeaderContent>,
      headerSx: WEEK_COUNT_COL_SX,
      cellSx: WEEK_COUNT_COL_SX,
      render: (row, rowIdx) => {
        const cell = resolveCell(weekdayMatrix[row.value], w.value, storeTotal);
        const tone = availabilityTone(cell.count, cell.total);
        const suffix = rowIdx === 0 ? " Stores" : "";
        return (
          <ListingCellLeft sx={{ color: AVAILABILITY_TONES[tone] }}>
            {`${cell.count}/${cell.total}${suffix}`}
          </ListingCellLeft>
        );
      },
    })),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6">
          {usedFor === "update" ? "Edit" : "Add"} Contact-based Visual: Detailed Weekday Selection
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select the weekdays on which the visual should be played.
          The cells display the number of selected stores that are open on each weekday for the corresponding calendar week.
        </Typography>

        {/* -------- Store Availability filter -------- */}
        <Box sx={{ mb: 2 }}>
          <SingleSelect
            label="Store Availability"
            usedFor="filter"
            allowClearance={false}
            disableSearch={true}
            options={STORE_AVAILABILITY_OPTIONS}
            defaultVal={availabilityFilter}
            selectionCheck={(option, value) => option.value === value.value}
            onChangeCall={setAvailabilityFilter}
          />
        </Box>

        <StoreAvailabilityLegend />

        {/* -------- Table -------- */}
        <ListingDataTableForModal
          columns={columns}
          rows={filteredRows}
          rowKey={(row) => row.value}
          emptyText="No weekdays available"
          fitContent
        />

        {/* -------- Footer: counter + download -------- */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <StyledDarkRedInformationTypography variant="body2">
            Selected {selectedInView} of {filteredRows.length}
          </StyledDarkRedInformationTypography>

          {filteredRows.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={-1}>
              <TextButton
                onClick={(e) => {
                  e.preventDefault();
                  downloadExcelFile();
                }}
                label="Download Excel"
                icon={<Download />}
              />
              <HtmlTooltip
                title={
                  <Box sx={{ maxWidth: 200 }}>
                    <Typography variant="body2">
                      The export reflects the current state of data at the time of download.
                      Data in CAP Explorer may change over time.
                    </Typography>
                  </Box>
                }
              >
                <IconContainer>
                  <Info fontSize="small" sx={{ marginTop: 1 }} />
                </IconContainer>
              </HtmlTooltip>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2}>
          <CloseButton onClick={onClose} label="Cancel" icon={<Close />} />
          <ActionButton
            onClick={handleApply}
            label="Apply"
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DetailedWeekdaySelection;
